import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { UserLoginService } from '../services/user-login.service';
import { environment } from '../../../environments/environment';
import { AuthRepoService, SimpleUserProfile } from '../../app-starter/login-secure/authrepo.service';
import { LoginSuccessBean } from '../dtos/loginSuccessBean';
import { recordAdminAuthTrace } from './admin-auth-trace';
import { isAdministratorTenantAdmin, isAdministratorTenantAgent } from '../utils/utils';

interface AdminOAuthProfile {
    sub: string;
    clientId?: string;
    roles?: string[] | string;
    tenants?: SimpleUserProfile[];
    agentId?: string;
    given_name?: string;
    name?: string;
}

export const AuthAdminGuard: CanActivateFn = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
) => {
    const userLoginService = inject(UserLoginService);
    const router = inject(Router);
    const http = inject(HttpClient);
    const authRepoService = inject(AuthRepoService);

    if (!window.location.hostname.includes(environment.administratorDomain) && window.location.hostname !== 'localhost') {
        recordAdminAuthTrace('Admin guard rejected hostname', { hostname: window.location.hostname });
        return false;
    }

    if (userLoginService.getIsAdminLoggedIn()) {
        recordAdminAuthTrace('Admin guard allowed existing session', { url: state.url });
        return true;
    }

    if (sessionStorage.getItem('authMode') === 'oauth') {
        recordAdminAuthTrace('Admin guard checking OAuth profile', { url: state.url });
        return http.get<AdminOAuthProfile>(`${environment.loginApi}/api/tenant/me`, { withCredentials: true }).pipe(
            map((userProfile) => {
                if (userProfile?.tenants && Array.isArray(userProfile.tenants)) {
                    recordAdminAuthTrace('Admin guard requires tenant selection', {
                        tenantCount: userProfile.tenants.length
                    });
                    authRepoService.tenantsList = {
                        sub: userProfile.sub,
                        tenants: userProfile.tenants
                    };
                    router.navigate([`/${environment.baseAdminPath}/login-secure`], {
                        queryParams: { openModal: 'true' }
                    });
                    return false;
                }

                const roles: string[] = Array.isArray(userProfile?.roles)
                    ? userProfile.roles
                    : typeof userProfile?.roles === 'string' ? [userProfile.roles] : [];
                const hasAdministratorTenantAdminAccess =
                    isAdministratorTenantAdmin(userProfile?.clientId, roles);
                const hasAdministratorTenantAgentAccess =
                    isAdministratorTenantAgent(userProfile?.clientId, roles);

                if (!userProfile?.sub
                    || (!hasAdministratorTenantAdminAccess && !hasAdministratorTenantAgentAccess)) {
                    recordAdminAuthTrace('Admin guard rejected OAuth profile', {
                        hasSubject: Boolean(userProfile?.sub),
                        hasAdministratorTenantAdminAccess,
                        hasAdministratorTenantAgentAccess,
                        roles
                    });
                    router.navigate([`/${environment.baseAdminPath}/login-secure`]);
                    return false;
                }

                const userName = userProfile.given_name || userProfile.name || userProfile.sub;
                const userId = userProfile.agentId || userProfile.sub;
                const loginSuccessBean: LoginSuccessBean = {
                    name: userName,
                    clientId: userProfile.clientId,
                    role: roles.join(','),
                    userId
                };

                userLoginService.setAdminClientId(environment.administratorClientId, false);
                userLoginService.setAdminDomain(window.location.host, false);
                userLoginService.setAdminUserObject('BUSINESS_ACCOUNT', loginSuccessBean);
                userLoginService.setAdminUserObject('AGENT', {
                    id: userId,
                    email: userProfile.sub,
                    name: userName,
                    roles
                });
                userLoginService.setAdminRole();
                userLoginService.setIsAdminLoggedIn(true, false);

                recordAdminAuthTrace('Admin guard initialized OAuth session', {
                    clientId: userProfile.clientId,
                    roles
                });
                return true;
            }),
            catchError((error) => {
                recordAdminAuthTrace('Admin guard OAuth profile lookup failed', {
                    status: error?.status,
                    message: error?.message
                });
                router.navigate([`/${environment.baseAdminPath}/login-secure`]);
                return of(false);
            })
        );
    }

    recordAdminAuthTrace('Admin guard sent unauthenticated user to login', { url: state.url });
    router.navigate(['/' + environment.baseAdminPath + '/login'], { queryParams: { returnUrl: state.url } }).then();
    return false;
};
